<template>
  <AppFileUploadFilder :filder="file"
                       :filder-name="fileName"
                       :filder-progress="file.progress"
                       :filder-status="file.status"
                       :filder-size="fileSize"
                       @cancelPendingFilder="onCancelPendingFile"
                       @cancelInProgressFilder="onCancelInProgressFile"
                       @redoFilder="onRedoFile"
  >
    <template v-if="showRedactAction" #successAction>
      <AppButton variant="outlined"
                 class="AppFileUploadFile__redactButton"
                 data-locator="upload-tray-redact-button"
                 @click="onRedactClick"
      >
        {{ $t('common.app-file-upload.AppFileUploadFilder.redact') }}
      </AppButton>
    </template>
  </AppFileUploadFilder>
  <DocumentsNotRedactableDialog
    v-if="notRedactableDialogIsOpen"
    :reasons="filteredRedactionBlockedReasons"
    @close="notRedactableDialogIsOpen = false"
  />
  <RedactStorageWarningDialog
    v-if="redactStorageWarningDialogIsOpen"
    @cancel="redactStorageWarningDialogIsOpen = false"
    @confirm="onConfirmRedactStorageWarning"
  />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import AppFileUploadFilder from '@/common/app-file-upload/AppFileUploadFilder.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import { getPdfContentType, track, USER_ROLE } from '@/common/pendo/agent'
import { fileNameWithoutExtension, formatFileSize, openFileViewer } from '@/common/utils/files'
import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
import { IGNORED_REDACTION_BLOCKED_REASONS, REDACT_WARNING_KEY } from '@/project/documents/constants/redaction'
import DocumentsNotRedactableDialog from '@/project/documents/dialogs/DocumentsNotRedactableDialog.vue'
import RedactStorageWarningDialog from '@/project/documents/dialogs/RedactStorageWarningDialog.vue'
import { AppUploadHandlerActionTypes } from '@/store/modules/app-upload-handler/action_types'

export default {
  name: 'AppFileUploadFile',
  components: { AppButton, AppFileUploadFilder, DocumentsNotRedactableDialog, RedactStorageWarningDialog },
  props: {
    file: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      notRedactableDialogIsOpen: false,
      redactStorageWarningDialogIsOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    fileName () {
      return fileNameWithoutExtension(this.file.fileObject.name)
    },
    fileSize () {
      return formatFileSize(this.file.fileObject.size)
    },
    uploadedDocument () {
      return this.file.uploadedDocument
    },
    showRedactAction () {
      return this.uploadedDocument?.ext === 'pdf' &&
        typeof this.uploadedDocument.isRedactable === 'boolean' &&
        !this.uploadedDocument.redactionBlockedReasons?.some(reason => IGNORED_REDACTION_BLOCKED_REASONS.includes(reason))
    },
    filteredRedactionBlockedReasons () {
      return (this.uploadedDocument?.redactionBlockedReasons ?? []).filter(
        reason => !IGNORED_REDACTION_BLOCKED_REASONS.includes(reason),
      )
    },
  },
  methods: {
    ...mapActions('appUploadHandler', [AppUploadHandlerActionTypes.CANCEL_FILE, AppUploadHandlerActionTypes.REDO_FILE_UPLOAD]),
    onCancelPendingFile () {
      this.CANCEL_FILE(this.file.key)
    },
    onCancelInProgressFile () {
      this.onCancelPendingFile()
    },
    onRedoFile () {
      this.REDO_FILE_UPLOAD(this.file.key)
    },
    onRedactClick () {
      if (!this.uploadedDocument) {
        return
      }
      if (!this.uploadedDocument.isRedactable) {
        this.notRedactableDialogIsOpen = true
        return
      }
      const hideWarning = getUserSetting(this.$store.getters['user/currentUserId'], REDACT_WARNING_KEY)
      if (!hideWarning) {
        this.redactStorageWarningDialogIsOpen = true
        return
      }
      this.openRedactViewer()
    },
    onConfirmRedactStorageWarning (hideChecked) {
      this.redactStorageWarningDialogIsOpen = false
      if (hideChecked) {
        setUserSetting(this.$store.getters['user/currentUserId'], REDACT_WARNING_KEY, true)
      }
      this.openRedactViewer()
    },
    openRedactViewer () {
      track('document_previewed', {
        document_id: this.uploadedDocument.id,
        project_mnemo: this.file.roomMnemo,
        user_role: this.isCurrentUserPm ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
        document_type: this.uploadedDocument.ext,
        entrypoint: 'upload_tray',
        pdf_content_type: getPdfContentType(this.uploadedDocument),
      })
      openFileViewer(this.file.roomMnemo, this.uploadedDocument.id, { isRedactMode: true, entrypoint: 'upload_tray', skipRedactWarning: true })
    },
  },
}
</script>

<style scoped lang="scss">
.AppFileUploadFile {
  &__redactButton {
    height: 28px;
    padding: 4px 12px !important;
    border-color: rgb(var(--v-theme-border-secondary)) !important;

    :deep(.v-btn__content) {
      font-weight: 700;
    }
  }
}
</style>
